import type { CriterionType, Recurrence, Tier, WindowType } from './types'

export const TIERS: Array<{ value: Tier; label: string; defaultPoints: number; subtitle: string }> =
  [
    { value: 'bronze', label: 'Bronze', defaultPoints: 5, subtitle: 'Primeiros passos' },
    { value: 'prata', label: 'Prata', defaultPoints: 10, subtitle: 'Comprometimento' },
    { value: 'ouro', label: 'Ouro', defaultPoints: 20, subtitle: 'Maestria' },
    { value: 'platina', label: 'Platina', defaultPoints: 50, subtitle: 'Raro · Tempo limitado' },
  ]

export const POINT_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50] as const

export const CRITERION_GROUPS: Array<{
  group: string
  items: Array<{ value: CriterionType; label: string; hint: string }>
}> = [
  {
    group: 'Conteúdo específico',
    items: [
      {
        value: 'specific_episode',
        label: 'Concluir um episódio específico',
        hint: 'O usuário precisa marcar 1 aula determinada como concluída.',
      },
      {
        value: 'specific_module',
        label: 'Concluir um módulo específico',
        hint: '100% dos episódios de um módulo escolhido.',
      },
      {
        value: 'specific_course',
        label: 'Concluir um curso específico',
        hint: 'Todos os módulos do curso completos.',
      },
    ],
  },
  {
    group: 'Quantidade (qualquer curso)',
    items: [
      {
        value: 'count_episodes',
        label: 'Concluir N episódios',
        hint: 'Conta episódios de qualquer curso.',
      },
      {
        value: 'count_modules',
        label: 'Concluir N módulos',
        hint: 'Módulos completos em qualquer curso.',
      },
      {
        value: 'count_courses',
        label: 'Concluir N cursos',
        hint: 'Cursos inteiros completos.',
      },
    ],
  },
  {
    group: 'Comportamental',
    items: [
      {
        value: 'any_activity',
        label: 'Qualquer atividade no dia',
        hint: 'Completar ≥ 1 episódio no dia (≈ ativo).',
      },
      {
        value: 'streak_days',
        label: 'Streak de N dias consecutivos',
        hint: 'Atividade em dias seguidos.',
      },
      {
        value: 'day_marathon',
        label: 'Maratona — N episódios no mesmo dia',
        hint: 'Concentração num único dia.',
      },
      {
        value: 'return_after_idle',
        label: 'Retorno após N+ dias inativo',
        hint: 'Volta depois de ausência.',
      },
      {
        value: 'account_created',
        label: 'Cadastro na plataforma',
        hint: 'Apenas existir (útil pra "First Wave").',
      },
    ],
  },
  {
    group: 'Quizzes',
    items: [
      {
        value: 'quiz_perfect',
        label: 'Gabaritar um quiz',
        hint: '100% em uma tentativa (opcionalmente em um quiz específico).',
      },
      {
        value: 'quiz_perfect_streak',
        label: 'N quizzes 100% seguidos',
        hint: 'Sequência consecutiva sem erro.',
      },
      {
        value: 'module_all_quizzes_perfect',
        label: 'Todos os quizzes de um módulo',
        hint: 'Gabaritar a totalidade do módulo (opcional escolher módulo).',
      },
      {
        value: 'quiz_comeback',
        label: 'Errar e voltar pra 100%',
        hint: 'Falhou antes, depois acertou tudo.',
      },
    ],
  },
  {
    group: 'Manual',
    items: [
      {
        value: 'manual_roster',
        label: 'Lista manual de usuários',
        hint: 'Você sobe um arquivo (.csv/.json) de emails; a badge é concedida direto, sem critério automático.',
      },
    ],
  },
]

export const WINDOW_OPTIONS: Array<{ value: WindowType; label: string; hint: string }> = [
  { value: 'always', label: 'Sempre vale', hint: 'Sem janela temporal.' },
  { value: 'between', label: 'Entre duas datas', hint: 'Início + fim fixos.' },
  { value: 'specific_date', label: 'Um único dia', hint: 'Apenas naquela data específica.' },
  { value: 'recurring_yearly', label: 'Recorrência anual', hint: 'Mesmo dia/mês todo ano.' },
  { value: 'recurring_monthly', label: 'Recorrência mensal', hint: 'Mesmo dia do mês.' },
  { value: 'recurring_weekly', label: 'Recorrência semanal', hint: 'Mesmo dia da semana.' },
]

export const RECURRENCE_OPTIONS: Array<{ value: Recurrence; label: string; hint: string }> = [
  { value: 'once', label: 'Uma vez', hint: 'Conquista única na vida.' },
  { value: 'per_year', label: 'A cada ano', hint: 'Reconquistável em cada ciclo anual.' },
  { value: 'per_month', label: 'A cada mês', hint: 'Reconquistável mensalmente.' },
  { value: 'per_week', label: 'A cada semana', hint: 'Reconquistável semanalmente.' },
]

export const WEEKDAYS = [
  { value: 1, label: 'Segunda-feira', short: 'SEG' },
  { value: 2, label: 'Terça-feira', short: 'TER' },
  { value: 3, label: 'Quarta-feira', short: 'QUA' },
  { value: 4, label: 'Quinta-feira', short: 'QUI' },
  { value: 5, label: 'Sexta-feira', short: 'SEX' },
  { value: 6, label: 'Sábado', short: 'SAB' },
  { value: 7, label: 'Domingo', short: 'DOM' },
] as const

export const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const
